/**
 * Enhanced authentication API with support for login attempt tracking and account lockout
 * @param {Object} ui - UI instance with http client and store
 * @returns {Object} Authentication API object
 */
const authentication = (ui) => {
  return {
    basic ({ username, password }) {
      return ui.http.post('/login', { username, password })
        .then(response => {
          if (!response) {
            return
          }

          const data = response.data || {}

          // Check for nested authentication errors in successful HTTP responses
          // Backend returns: { code: 0, message: "", data: { status_code: 423, locked_until: "...", ... } }
          const nestedData = data.data || {}
          const actualStatus = nestedData.status_code

          // If there's a nested status code, check if it indicates an authentication error
          if (actualStatus && actualStatus !== 200) {

            let enhancedError

            switch (actualStatus) {
              case 401:
                // Failed login attempts - extract attempt information from nested data
                const failedAttempts = nestedData.failed_attempts || 0
                const remainingAttempts = nestedData.remaining_attempts || 0

                enhancedError = {
                  code: 401,
                  message: nestedData.message || `Invalid credentials. ${failedAttempts} failed attempt(s). ${remainingAttempts} attempt(s) remaining before account lockout.`,
                  type: 'error',
                  enhanced: true,
                  loginAttempts: {
                    failed: failedAttempts,
                    remaining: remainingAttempts
                  },
                  skipUINotification: false
                }
                break

              case 423:
                // Account locked - extract lockout information from nested data
                const lockedUntil = nestedData.locked_until
                const unlockTimeRemaining = nestedData.unlock_time_remaining
                const message = nestedData.message || 'Account temporarily locked due to too many failed login attempts.'

                enhancedError = {
                  code: 423,
                  message: message,
                  type: 'error',
                  enhanced: true,
                  accountLocked: {
                    lockedUntil: lockedUntil,
                    unlockTimeRemaining: unlockTimeRemaining
                  },
                  skipUINotification: false
                }
                break

              default:
                // Other authentication errors
                enhancedError = {
                  code: actualStatus,
                  message: nestedData.message || `Authentication failed with status ${actualStatus}.`,
                  type: 'error'
                }
            }

            // Add enhanced flag for notification system
            enhancedError.enhanced = true

            // Add flag to prevent UI library from creating duplicate notifications
            // All enhanced errors should show notifications through the enhanced system
            throw enhancedError
          }

          // Handle successful login (200 with token)
          if (response.status === 200 && data && data.token) {
            return data
          }

          // Handle successful login (nested 200 with token)
          if (actualStatus === 200 && nestedData && nestedData.token) {
            const successResponse = {
              data: {
                token: nestedData.token
              }
            }

            return successResponse
          }

          // Handle other success cases
          return data
        })
        .catch(error => {
          // Check if this is an enhanced authentication error thrown from the .then() block
          if (error && error.enhanced && (error.loginAttempts || error.accountLocked)) {
            // This is our enhanced error from the .then() block, re-throw it unchanged
            throw error
          }

          if (!error.response) {
            // Check if this is already an enhanced error (but not authentication-specific)
            if (error && error.enhanced) {
              throw error
            }

            // True network error or other non-HTTP error
            const networkError = {
              code: 500,
              message: 'Network error. Please check your connection and try again.',
              type: 'error',
              enhanced: true,
              // Network errors during authentication should show notifications
              skipUINotification: false
            }

            throw networkError
          }

          const status = error.response.status
          const data = error.response.data || {}

          // For HTTP errors, create a generic error response
          const enhancedError = {
            code: status,
            message: data.message || `HTTP error ${status}. Please try again.`,
            type: 'error',
            enhanced: true,
            // Only skip UI notifications for non-authentication errors
            // Authentication errors (401) should show notifications
            skipUINotification: status !== 401
          }

          throw enhancedError
        })
    }
  }
}

export { authentication }
